<template>
  <lf-card
    class="mb-8 py-6"
    :has-padding="false"
    data-cy="business-notes-panel"
  >
    <loader :is-loading="isFetching" />
    <template #header>
      <div class="flex items-center justify-between px-6">
        <div class="flex items-center pl-4">
          <lf-h2 class="-mb-0">
            {{ $t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.NOTES") }}
          </lf-h2>
          <div
            v-if="numOfNotes"
            class="ml-1 h-5 w-5 justify-center rounded-full bg-gray-400/20 pt-px text-center text-xs text-gray-800"
          >
            {{ numOfNotes }}
          </div>
        </div>
      </div>
    </template>
    <div class="min-h-120">
      <deal-notes-input
        class="mx-6"
        :note-to-edit="noteToEdit || undefined"
        disable-type-selector
        :note-type="NoteType.Business"
        :is-all-activity="!dealActivityActiveTypes.length"
        :auto-focus="false"
        @edit-note:unset="setEditNote(null)"
      />
      <div
        v-if="numOfNotes"
        ref="container"
        class="mt-2 flex h-full w-full flex-col space-y-8! overflow-y-auto rounded-b bg-grey-hover px-6 py-4"
      >
        <note-view
          v-for="note in businessNotes"
          :id="`note-${note.id}`"
          :key="note.id"
          :note="note"
          :is-all-activity="!dealActivityActiveTypes.length"
          :editing-note="noteToEdit?.id === note.id"
          class="read-note rounded-md transition-colors duration-500 ease-in-out"
          @note:edit="setEditNote"
        />
      </div>
      <div
        v-else
        class="mt-10 flex h-full flex-col items-center justify-center"
      >
        <icon-base height="70" width="70" :icon="IconNotesEmpty" />
        <lf-h3 class="mt-5">
          {{ $t("DEALS.BUSINESS_NOTES.EMPTY_STATE_TITLE") }}
        </lf-h3>
        <div class="mt-3 px-5 text-center text-base">
          {{ $t("DEALS.BUSINESS_NOTES.EMPTY_STATE_DESCRIPTION") }}
        </div>
      </div>
    </div>
  </lf-card>
</template>
<script setup lang="ts">
import { useInfiniteScroll } from "@vueuse/core";
import { computed, onMounted, ref, toRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import orderBy from "lodash/orderBy";
import { storeToRefs } from "pinia";
import queryString from "query-string";

import { CommunicationType } from "@/enums/communicationLogs";
import { NoteType } from "@/enums/dealActivity";
import { DEFAULT_PER_PAGE } from "@/helpers/constants";
import { useDeals } from "@/hooks/deals";
import { useCommunicationLogsQuery } from "@/hooks/queries/useInbox";
import { useCommunicationStore } from "@/stores/communication";

import IconNotesEmpty from "@/components/icons/IconNotesEmpty.vue";
import Loader from "@/components/ui/Loader.vue";
import DealNotesInput from "@/views/deals/components/dealNotes/DealNotesInput.vue";
import NoteView from "@/views/deals/components/dealNotes/Note.vue";

import type { NoteLogData } from "@/models/communicationLogs";
import type { Note } from "@/models/notes";

const { activeDeal } = useDeals();
const route = useRoute();
const router = useRouter();
const communicationStore = useCommunicationStore();
const { hasNewLogs, dealActivityActiveTypes } = storeToRefs(communicationStore);

const container = ref<HTMLDivElement | null>(null);
const noteToEdit = ref<Note | null>(null);
const initialParams = queryString?.parse(location.search);

const {
  data,
  isPending,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  refetch: fetchBusinessNotes,
} = useCommunicationLogsQuery(
  toRef({
    application_ids: [activeDeal.value?.id],
    type: [CommunicationType.note],
    note_type: NoteType.Business,
    per_page: DEFAULT_PER_PAGE.toString(),
  })
);

useInfiniteScroll(
  container,
  () => {
    if (!isPending.value && !isFetchingNextPage.value) {
      fetchNextPage();
    }
  },
  {
    distance: 100,
    canLoadMore: () => hasNextPage.value,
  }
);

const noteLogs = computed(() => {
  const flattenedLogs = data.value?.pages.flatMap(({ data }) => data) || [];
  return flattenedLogs.map(({ data }) => data).filter(Boolean);
});

const businessNotes = computed(() => {
  return orderBy(noteLogs.value, "id").reverse() as NoteLogData[];
});
const numOfNotes = computed(() => data.value?.pages[0]?.meta?.total ?? 0);

const setEditNote = (note: Note | null) => {
  noteToEdit.value = note;
};

const scrollNoteIntoView = () => {
  const note = document.getElementById(
    initialParams?.businessNote as string
  ) as HTMLDivElement;
  if (!note) {
    return;
  }
  note.scrollIntoView({
    block: "start",
    inline: "nearest",
    behavior: "smooth",
  });
  setTimeout(() => {
    note.classList.add("bg-blue-50");
  }, 300);
  setTimeout(() => {
    note.classList.remove("bg-blue-50");
  }, 3000);
};

onMounted(async () => {
  if (initialParams.businessNote !== undefined) {
    scrollNoteIntoView();
  }
  const { path, query } = route;
  router?.replace({ path, query });
});

watch(hasNewLogs, async (updatedValue, oldValue) => {
  if (updatedValue && !oldValue) {
    await fetchBusinessNotes();
  }
});
</script>
